import { useMultipleFeatureFlags } from '@agentnet/feature-flags';
import { DocumentsEmptyState, QuotesEmptyState } from '@fluentsms/agentnet-web-icons';
import {
  NON_FA_UW_WIRE_VALIDATION_HEADING,
  WIRE_VALIDATION_MESSAGE,
  WIRE_VALIDATION_PRODUCT_CODE,
} from 'features/help/constants';
import NonFaUwEmptyState from 'features/services/shared/NonFaUwEmptyState';
import VerificationEmptyState from 'features/services/shared/VerificationEmptyState';
import { Link as RouterLink } from 'react-router-dom';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';

interface WireVerificationEmptyStateProps {
  isWireValidationEnabled?: boolean;
  hasWireValidationRights?: boolean;
  agentNetAdmin?: boolean;
  onNewValidation: () => void;
}

const WireVerificationEmptyState = ({
  isWireValidationEnabled,
  hasWireValidationRights,
  agentNetAdmin,
  onNewValidation,
}: WireVerificationEmptyStateProps) => {
  const [nonFaUw] = useMultipleFeatureFlags(FeatureFlag.NON_FA_UW_ANS_679_ENABLED);
  const isNonFaUwEnabled = nonFaUw.ready && !!nonFaUw.value;

  const legacyAdminMessage = (
    <>
      Ready to start validating wires?{' '}
      {agentNetAdmin ? (
        <>
          Enable{' '}
          <RouterLink
            to={`/admin/integrations-features?productCode=${WIRE_VALIDATION_PRODUCT_CODE}`}
            data-testid="wire-validation-link-enable"
          >
            Wire Validation
          </RouterLink>{' '}
          to get started.
        </>
      ) : (
        'Ask your admin to enable Wire Validation to get started.'
      )}
    </>
  );

  if (isNonFaUwEnabled) {
    // Wire Validation is always available for First American, so the firm level status
    // reports whether it has been enabled for the firm's other underwriters.
    const disabledProducts = isWireValidationEnabled
      ? []
      : [
          {
            name: 'Wire Validation',
            productCode: WIRE_VALIDATION_PRODUCT_CODE,
            linkTestId: 'wire-validation-link-enable',
          },
        ];

    return (
      <NonFaUwEmptyState
        icon={<QuotesEmptyState />}
        headerText="No Wire Validations"
        descriptionText='Start validating wires by clicking "New Validation" below.'
        actionLabel="New Validation"
        onAction={onNewValidation}
        isActionDisabled={!hasWireValidationRights}
        actionTestId="wire-validation-new-validation-button-bottom"
        agentNetAdmin={agentNetAdmin}
        promptHeading={NON_FA_UW_WIRE_VALIDATION_HEADING}
        disabledProducts={disabledProducts}
        promptTestId="wire-validation-non-fa-underwriter-prompt"
        containerTestId="wire-validation-empty-state"
      />
    );
  }

  return (
    <VerificationEmptyState
      icon={<DocumentsEmptyState />}
      headerText={isWireValidationEnabled ? 'No Wire Validations' : 'Validate Wires with Confidence'}
      descriptionText={
        isWireValidationEnabled ? 'Start validating wires by clicking "New Validation" below.' : WIRE_VALIDATION_MESSAGE
      }
      isProductEnabled={isWireValidationEnabled}
      actionLabel="New Validation"
      onAction={onNewValidation}
      isActionDisabled={!hasWireValidationRights}
      actionTestId="wire-validation-new-validation-button-bottom"
      adminMessage={legacyAdminMessage}
      containerTestId="wire-validation-empty-state"
    />
  );
};

export default WireVerificationEmptyState;
